/**
 * The Delivery tab: where the estimate plans to ship, in the order page's
 * delivery-line shape (without a status column — nothing has shipped), then
 * the customer address the estimate recorded, kept visibly apart.
 */
import type { EstimateDetailViewItem } from "@shared-core/data/api-types";
import {
  courierProvenanceLabel,
  courierSummary,
  deliveryAddressPresentation,
  estimateCustomerAddressPresentation,
} from "@shared-core/domain/estimate-presentation";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../../app/components/primitives/table";
import { InfoTip } from "../../app/components/patterns/InfoTip";
import {
  AddressBlock,
  formatDateOnly,
  Gap,
  Section,
  TabContainer,
} from "./atoms";

export function DeliveryTab({
  estimate,
}: {
  estimate: EstimateDetailViewItem;
}) {
  const { delivery, customer_address } = estimate.delivery_billing;
  const address = deliveryAddressPresentation(delivery);
  const courier = courierSummary(delivery.courier);
  const recorded =
    delivery.coverage === "per_estimate"
      ? "Delivery details recorded on this estimate."
      : delivery.coverage === "customer_default"
        ? "Delivery details come from the customer's defaults."
        : "No delivery details are recorded.";
  const customerAddress = estimateCustomerAddressPresentation(customer_address);
  return (
    <TabContainer label="Delivery details">
      <Section title="Planned delivery" label="Delivery" info={recorded}>
        <Table className="text-sm [&_td]:pr-8 [&_th]:pr-8">
          <TableHeader>
            <TableRow>
              <TableHead>Line</TableHead>
              <TableHead className="text-right">Quantity</TableHead>
              <TableHead>Going to</TableHead>
              <TableHead>Courier</TableHead>
              <TableHead>Required by</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            <TableRow>
              <TableCell className="align-top tabular-nums">1</TableCell>
              <TableCell className="align-top text-right tabular-nums">
                {estimate.specification.quantity?.toLocaleString("en-GB")}
              </TableCell>
              <TableCell className="max-w-md whitespace-normal align-top">
                {address.kind === "address" ? (
                  <span className="inline-flex items-start gap-1.5">
                    <AddressBlock address={address.address} />
                    <InfoTip label="Where this address came from">
                      {address.provenanceLabel}
                    </InfoTip>
                  </span>
                ) : (
                  <span>
                    <Gap>No delivery address</Gap>{" "}
                    <InfoTip label="Why there is no delivery address">
                      {address.explanation}
                    </InfoTip>
                  </span>
                )}
              </TableCell>
              <TableCell className="whitespace-normal align-top">
                {courier ? (
                  <>
                    {courier}{" "}
                    <InfoTip label="Where the courier came from">
                      {courierProvenanceLabel(delivery.courier_provenance)}
                    </InfoTip>
                  </>
                ) : (
                  <Gap>No courier recorded</Gap>
                )}
              </TableCell>
              <TableCell className="align-top">
                {/* Tharstern's own production deadline, replicated as
                    `required_date` — date-only (audit finding 87). */}
                {formatDateOnly(delivery.required_by) ?? (
                  <Gap>No required-by date</Gap>
                )}
              </TableCell>
            </TableRow>
          </TableBody>
        </Table>
      </Section>
      {/* The customer address the estimate itself recorded. Kept apart from
          the destination and worded for what it is *not*: Tharstern
          snapshots the customer's own address onto the quote, and the
          destination is a separate fact. Rendering this as "Going to" would
          ship a print job to a head office that never ordered it. */}
      <Section
        title="Customer address on this estimate"
        label="Customer address on this estimate"
      >
        {customerAddress.kind === "address" ? (
          <div className="text-sm">
            {customerAddress.lines.map((line) => (
              <div key={line}>{line}</div>
            ))}
            <p className="mt-1 text-muted-foreground">{customerAddress.note}</p>
          </div>
        ) : (
          <p className="text-sm">
            <Gap>No customer address recorded</Gap>{" "}
            <InfoTip label="Why there is no customer address">
              {customerAddress.explanation}
            </InfoTip>
          </p>
        )}
      </Section>
    </TabContainer>
  );
}
